import { create } from "zustand"
import { devtools } from "zustand/middleware"
import { watchAccount } from "@wagmi/core"
import { ConnectEthereumUseCaseInput } from "../../domain/ethereum/UseCases/ConnectEthereumUseCase"
import { WalletState } from "../../domain/ethereum/Models/WalletState"
import { publicClient } from "../../js/viem"
import { wagmiConfig } from "../../js/wagmi"
import { useUserInfoStore } from "./userInfoStore"

// Make sure global objects are available
window.publicClient = window.publicClient ?? publicClient
window.wagmiConfig = window.wagmiConfig ?? wagmiConfig

interface EthereumState {
  wallet: WalletState
  error?: string
  _isWatching: boolean
  _hasInitialized: boolean
}

interface EthereumActions {
  getOrFetch: () => Promise<WalletState>
  connectWallet: ({ connector }: ConnectEthereumUseCaseInput) => Promise<WalletState>
  disconnectWallet: () => Promise<WalletState>
  switchChain: () => Promise<WalletState>
  register: ({ username }: { username: string }) => Promise<WalletState>
  login: () => Promise<WalletState>
  reconnect: () => Promise<WalletState>
  reconnectOrLogin: () => Promise<WalletState>
  waitForNextBlockNumber: () => Promise<number>
  updateWalletState: () => Promise<WalletState | undefined>
  setLoadingState: () => void
  setEmptyState: () => void
  reset: () => void
  initializeWallet: () => void
  startWatching: () => (() => void) | undefined
  stopWatching: () => void
  startBlockNumberWatching: (isVisible: boolean) => (() => void) | undefined
}

type EthereumStore = EthereumState & EthereumActions

export const useEthereumStore = create<EthereumStore>()(
  devtools(
    (set, get) => ({
      wallet: WalletState.empty(),
      error: undefined,
      _isWatching: false,
      _hasInitialized: false,

      getOrFetch: async () => {
        const state = get()
        if (state.wallet.isConnected) return state.wallet
        const result = await state.updateWalletState()
        return result ?? state.wallet
      },

      setLoadingState: () => {
        const { wallet } = get()
        if (wallet.isConnecting) return
        set({ wallet: WalletState.create({ status: WalletState.STATUS.CONNECTING }) })
      },

      setEmptyState: () => {
        set({ wallet: WalletState.empty() })
      },

      reset: () => {
        set({ wallet: WalletState.empty() })
      },

      updateWalletState: async () => {
        const { wallet } = get()

        if (wallet.isConnecting) return

        return window.domain.GetWalletStateEthereumUseCase.execute()
          .then(({ walletState: updatedWallet }) => {
            if (wallet.account && wallet.account !== updatedWallet.account) {
              window.location.reload()
            }

            const currentWallet = get().wallet
            if (currentWallet.serialize() === updatedWallet.serialize()) return

            set({ wallet: updatedWallet })
            return updatedWallet
          })
          .catch((error) => {
            get().setEmptyState()
            throw error
          })
      },

      connectWallet: async ({ connector }: ConnectEthereumUseCaseInput) => {
        get().setLoadingState()
        return window.domain.ConnectEthereumUseCase.execute({ connector })
          .then(({ walletState: wallet }) => {
            set({ wallet })
            return wallet
          })
          .catch((error) => {
            get().setEmptyState()
            throw error
          })
      },

      disconnectWallet: async () => {
        return window.domain.DisconnectEthereumUseCase.execute()
          .then(({ walletState: wallet }) => {
            set({ wallet })
            window.location.reload()
            return wallet
          })
          .catch((error) => {
            get().setEmptyState()
            throw error
          })
      },

      switchChain: async () => {
        return window.domain.SwitchChainEthereumUseCase.execute()
          .then(({ walletState: wallet }) => {
            set({ wallet })
            return wallet
          })
          .catch((error) => {
            get().setEmptyState()
            throw error
          })
      },

      register: async ({ username }: { username: string }) => {
        get().setLoadingState()
        return window.domain.RegisterEthereumUseCase.execute({ username })
          .then(async ({ walletState: wallet }) => {
            set({ wallet })

            if (wallet.account) {
              await useUserInfoStore.getState().createWallet(wallet.account)
            }

            return wallet
          })
          .catch((error) => {
            get().setEmptyState()
            throw error
          })
      },

      login: async () => {
        const { wallet } = get()
        if (wallet.isConnecting) return wallet

        set({ error: undefined })
        get().setLoadingState()
        return window.domain.LoginEthereumUseCase.execute()
          .then(({ walletState: wallet }) => {
            set({ wallet })
            return wallet
          })
          .catch((error) => {
            set({ wallet: WalletState.empty(), error: error.message })
            throw error
          })
      },

      reconnect: async () => {
        get().setLoadingState()
        return window.domain.ReconnectEthereumUseCase.execute()
          .then(({ walletState }) => {
            set({ wallet: walletState })
            return walletState
          })
          .catch((error) => {
            get().setEmptyState()
            throw error
          })
      },

      reconnectOrLogin: async () => {
        const walletState = await get().reconnect()

        if (walletState.isConnected) {
          return walletState
        }

        return get().login()
      },

      waitForNextBlockNumber: async (): Promise<number> => {
        return new Promise((resolve, reject) => {
          let currentBlockNumber: number | undefined

          try {
            // Start watching for block numbers
            const unwatch = window.publicClient.watchBlockNumber({
              onBlockNumber: (blockNumber) => {
                if (currentBlockNumber === undefined) {
                  // Capture the initial block number
                  currentBlockNumber = Number(blockNumber)
                } else if (blockNumber > currentBlockNumber) {
                  // Resolve only when the block number increments
                  unwatch()
                  resolve(Number(blockNumber))
                }
              },
              onError: (error) => {
                unwatch()
                reject(error)
              },
            })
          } catch (error) {
            reject(error)
          }
        })
      },

      initializeWallet: () => {
        const state = get()

        if (state._hasInitialized) return

        set({ _hasInitialized: true })

        get().setLoadingState()
        window.domain.ReconnectEthereumUseCase.execute()
          .then(({ walletState }) => set({ wallet: walletState }))
          .catch(console.error)
      },

      startWatching: () => {
        const state = get()

        if (state.wallet.isAbstracted) return
        if (state._isWatching) return

        set({ _isWatching: true })

        return watchAccount(window.wagmiConfig, {
          onChange() {
            const currentState = get()
            if (!currentState.wallet.isConnecting) {
              currentState.updateWalletState()
            }
          },
        })
      },

      stopWatching: () => {
        set({ _isWatching: false })
      },

      startBlockNumberWatching: (isVisible: boolean) => {
        if (!isVisible) return undefined

        const unwatch = window.publicClient.watchBlockNumber({
          onBlockNumber: (blockNumber) => {
            window.blockNumber = Number(blockNumber)
          },
        })

        return unwatch
      },
    }),
    { name: "EthereumStore" },
  ),
)
